/**
 * TDEE Calculator — Light DOM web component.
 *
 * Mifflin-St Jeor BMR (or Katch-McArdle when BF% given),
 * TDEE via activity multiplier, goal-based target, macros.
 */

const KG_PER_LB = 0.45359237
const CM_PER_IN = 2.54
const DEBOUNCE_MS = 350
const COUNTUP_MS = 1800
const ROW_DELAY = 260
const INNER_DELAY = 280

const GOAL_ADJUST = { lose: -500, maintain: 0, gain: 300 }
const MACRO_SPLIT = { protein: 0.3, carbs: 0.4, fat: 0.3 }
const CAL_PER_G = { protein: 4, carbs: 4, fat: 9 }

class FelixTdeeCalculator extends HTMLElement {
	connectedCallback() {
		this._timer = null
		this._hasCalculated = false
		this._isMetric = false
		this._sex = 'female'
		this._goal = 'maintain'
		this.cacheElements()
		this.bindEvents()
		this.maybeShowPresets()
	}

	disconnectedCallback() {
		this.unbindEvents()
	}

	cacheElements() {
		const $ = (s) => this.querySelector(s)
		const $$ = (s) => [...this.querySelectorAll(s)]

		this.sexRadios = $$('[name="tdee-sex"]')
		this.goalRadios = $$('[name="tdee-goal"]')
		this.age = $('[name="tdee-age"]')
		this.ft = $('[name="tdee-feet"]')
		this.inch = $('[name="tdee-inches"]')
		this.cm = $('[name="tdee-height-cm"]')
		this.lb = $('[name="tdee-pounds"]')
		this.kg = $('[name="tdee-weight-kg"]')
		this.bf = $('[name="tdee-body-fat"]')
		this.act = $('[name="tdee-activity"]')

		this.imperialGroups = $$('[data-unit-group="imperial"]')
		this.metricGroups = $$('[data-unit-group="metric"]')
		this.switchLinks = $$('.form-input__switch-link')

		this.invalidEl = $('.tdee-result__invalid')
		this.rows = $$('.tdee-result__row')
		this.cta = $('.tdee-calculator__cta')

		this.tdeeCals = $('[data-result="tdee"]')
		this.targetCals = $('[data-result="target"]')
		this.targetDesc = $('[data-result="target-desc"]')
		this.proteinEl = $('[data-result="protein"]')
		this.carbsEl = $('[data-result="carbs"]')
		this.fatEl = $('[data-result="fat"]')

		$$('input[type="number"]').forEach((el) => {
			el.addEventListener('wheel', (e) => e.preventDefault(), { passive: false })
		})
	}

	bindEvents() {
		this._trigger = () => {
			clearTimeout(this._timer)
			this._timer = setTimeout(() => {
				if (this._hasCalculated || this.readyToCalc()) this.doCalc()
			}, DEBOUNCE_MS)
		}
		this._onSwitch = (e) => {
			e.preventDefault()
			this.setUnits(e.target.dataset.switchTo === 'metric')
			if (this._hasCalculated) this.doCalc()
		}
		this._onSexChange = (e) => {
			this._sex = e.target.value
			if (this._hasCalculated) this.doCalc()
		}
		this._onGoalChange = (e) => {
			this._goal = e.target.value
			if (this._hasCalculated) this.doCalc()
		}

		const inputs = [this.age, this.ft, this.inch, this.cm, this.lb, this.kg, this.bf]
		inputs.forEach((el) => {
			if (!el) return
			el.addEventListener('input', this._trigger)
			el.addEventListener('change', this._trigger)
		})

		if (this.act) {
			this.act.addEventListener('change', this._trigger)
			this.act.addEventListener('input', this._trigger)
		}
		this.sexRadios.forEach((r) => r.addEventListener('change', this._onSexChange))
		this.goalRadios.forEach((r) => r.addEventListener('change', this._onGoalChange))
		this.switchLinks.forEach((link) => link.addEventListener('click', this._onSwitch))
	}

	unbindEvents() {
		const inputs = [this.age, this.ft, this.inch, this.cm, this.lb, this.kg, this.bf]
		inputs.forEach((el) => {
			if (!el) return
			el.removeEventListener('input', this._trigger)
			el.removeEventListener('change', this._trigger)
		})
		if (this.act) {
			this.act.removeEventListener('change', this._trigger)
			this.act.removeEventListener('input', this._trigger)
		}
		this.sexRadios.forEach((r) => r.removeEventListener('change', this._onSexChange))
		this.goalRadios.forEach((r) => r.removeEventListener('change', this._onGoalChange))
		this.switchLinks.forEach((link) => link.removeEventListener('click', this._onSwitch))
	}

	setUnits(metric) {
		this._isMetric = !!metric
		this.imperialGroups.forEach((g) => g.classList.toggle('hidden', this._isMetric))
		this.metricGroups.forEach((g) => g.classList.toggle('hidden', !this._isMetric))
	}

	readyToCalc() {
		const a = +this.age?.value || 0
		const actVal = +this.act?.value || 0
		const haveH = this._isMetric ? +this.cm?.value > 0 : (+this.ft?.value > 0 || +this.inch?.value > 0)
		const haveW = this._isMetric ? +this.kg?.value > 0 : +this.lb?.value > 0
		return a > 0 && haveH && haveW && actVal > 0
	}

	doCalc() {
		if (!this.readyToCalc()) return

		const a = +this.age.value
		const actVal = +this.act.value
		const bfVal = +this.bf?.value || 0
		let cmVal, kgVal

		if (!this._isMetric) {
			cmVal = (+this.ft?.value * 12 + (+this.inch?.value || 0)) * CM_PER_IN
			kgVal = (+this.lb?.value || 0) * KG_PER_LB
		} else {
			cmVal = +this.cm?.value || 0
			kgVal = +this.kg?.value || 0
		}

		if (!kgVal || !cmVal) return

		const bmr = bfVal > 0
			? 370 + 21.6 * (kgVal * (1 - bfVal / 100))
			: 10 * kgVal + 6.25 * cmVal - 5 * a + (this._sex === 'male' ? 5 : -161)

		const tdee = Math.round(bmr * actVal)
		const target = Math.round(tdee + (GOAL_ADJUST[this._goal] || 0))

		if (tdee < 0 || target < 0) {
			this.showInvalid()
			return
		}

		const protein = Math.round((target * MACRO_SPLIT.protein) / CAL_PER_G.protein)
		const carbs = Math.round((target * MACRO_SPLIT.carbs) / CAL_PER_G.carbs)
		const fat = Math.round((target * MACRO_SPLIT.fat) / CAL_PER_G.fat)

		this.showResults(tdee, target, protein, carbs, fat)

		// Once per page view, and deliberately without any entered value:
		// height, weight, age and sex are sensitive personal information.
		if (!this._hasCalculated) {
			window.felixTrack?.('calculator_complete', { calculatorName: 'tdee_calculator' })
		}

		this._hasCalculated = true
	}

	showInvalid() {
		this.invalidEl?.classList.add('is-visible')
	}

	showResults(tdee, target, protein, carbs, fat) {
		this.invalidEl?.classList.remove('is-visible')

		const goalLabels = { lose: 'Lose weight \u2212 500 cal/day', maintain: 'Maintain current weight', gain: 'Gain muscle + 300 cal/day' }
		if (this.targetDesc) this.targetDesc.textContent = goalLabels[this._goal] || ''

		// Target row bg by goal.
		const targetRow = this.rows[1]
		if (targetRow) {
			if (this._goal === 'lose') targetRow.style.backgroundColor = '#F7FAFB'
			else if (this._goal === 'gain') targetRow.style.backgroundColor = '#F0F7F0'
			else targetRow.style.backgroundColor = ''
		}

		// Staggered row reveal.
		const cals = [tdee, target]
		this.rows.forEach((row, i) => {
			setTimeout(() => {
				setTimeout(() => {
					const copy = row.querySelector('.tdee-result__copy')
					if (copy) copy.style.opacity = '1'
					const calEl = row.querySelector('.tdee-result__cals')
					if (calEl) {
						calEl.style.color = '#31302F'
						this.countUp(calEl, cals[i])
					}
				}, INNER_DELAY)
			}, i * ROW_DELAY)
		})

		if (this.proteinEl) { this.proteinEl.style.color = '#31302F'; this.countUp(this.proteinEl, protein, 'g') }
		if (this.carbsEl) { this.carbsEl.style.color = '#31302F'; this.countUp(this.carbsEl, carbs, 'g') }
		if (this.fatEl) { this.fatEl.style.color = '#31302F'; this.countUp(this.fatEl, fat, 'g') }

		this.showCta()
	}

	showCta() {
		const cta = this.cta
		if (!cta || cta.dataset.shown === 'true') return
		cta.dataset.shown = 'true'
		cta.classList.remove('hidden')
		requestAnimationFrame(() => {
			const full = cta.scrollHeight || 200
			cta.style.height = '0'
			requestAnimationFrame(() => {
				cta.style.height = full + 'px'
				setTimeout(() => {
					cta.style.opacity = '1'
					cta.style.pointerEvents = 'auto'
					cta.style.height = 'auto'
				}, 350)
			})
		})
	}

	maybeShowPresets() {
		const host = location.hostname || ''
		const isLocal = host === 'localhost' || host.endsWith('.local') || host === '127.0.0.1'
		const isStaging = host.includes('staging') || host.includes('.webflow.io')
		if (!isLocal && !isStaging) return

		const wrap = document.createElement('div')
		wrap.className = 'tdee-calculator__presets mt-16'
		wrap.innerHTML =
			'<div class="form-input__label-row flex justify-between align-center mb-12">' +
				'<span class="form-input__label text-body-md text-slate-40">Debug presets (local &amp; staging only)</span>' +
			'</div>' +
			'<div class="tdee-calculator__presets-buttons flex gap-8 flex-wrap">' +
				[
					['male_avg', 'Avg Male'],
					['female_avg', 'Avg Female'],
					['male_fit', 'Fit Male'],
					['female_active', 'Active Female'],
				]
					.map(
						([k, l]) =>
							`<button type="button" class="text-body-sm py-10 px-14 bg-slate-95 rounded-sm cursor-pointer flex-1" data-preset="${k}">${l}</button>`
					)
					.join('') +
			'</div>'

		const p = wrap.querySelector('.tdee-calculator__presets-buttons')
		this.querySelector('.tdee-calculator__form')?.appendChild(wrap)

		p.addEventListener('click', (e) => {
			const btn = e.target.closest('[data-preset]')
			if (!btn) return
			const presets = {
				male_avg: { sex: 'male', a: 32, ft: 5, inch: 11, lb: 200, act: '1.55', goal: 'maintain' },
				female_avg: { sex: 'female', a: 28, ft: 5, inch: 5, lb: 165, act: '1.375', goal: 'lose' },
				male_fit: { sex: 'male', a: 30, ft: 5, inch: 10, lb: 175, act: '1.725', goal: 'gain' },
				female_active: { sex: 'female', a: 25, ft: 5, inch: 7, lb: 140, act: '1.725', goal: 'maintain' },
			}
			const d = presets[btn.dataset.preset]
			if (!d) return

			const sv = (el, v) => { if (el) el.value = v }
			sv(this.age, d.a)
			sv(this.ft, d.ft)
			sv(this.inch, d.inch)
			sv(this.lb, d.lb)
			sv(this.act, d.act)

			sv(this.cm, Math.round((d.ft * 12 + d.inch) * CM_PER_IN))
			sv(this.kg, Math.round(d.lb * KG_PER_LB))

			this._sex = d.sex
			this.sexRadios.forEach((r) => { r.checked = r.value === d.sex })

			this._goal = d.goal
			this.goalRadios.forEach((r) => { r.checked = r.value === d.goal })

			this.setUnits(false)
			this._hasCalculated = true
			this.doCalc()
		})
	}

	countUp(el, end, suffix) {
		const start = Math.max(0, end - Math.max(50, end * 0.3))
		const t0 = performance.now()
		const ease = (t) => 1 - Math.pow(1 - t, 3)
		const run = (el._rid = (el._rid || 0) + 1)
		const step = (t) => {
			if (el._rid !== run) return
			const p = Math.min((t - t0) / COUNTUP_MS, 1)
			const v = Math.floor(start + (end - start) * ease(p))
			el.textContent = v.toLocaleString() + (suffix || '')
			if (p < 1) requestAnimationFrame(step)
		}
		requestAnimationFrame(step)
	}
}

customElements.define('felix-tdee-calculator', FelixTdeeCalculator)
